import React from "react";
import ReactExport from "react-data-export";
import styled from "@emotion/styled";
import { Button } from "@/components/Buttons";
import { formatDate } from "@/lib/date";

import { useProfileStore } from "@/stores/hooks";

const ExcelFile = ReactExport.ExcelFile;
const ExcelSheet = ReactExport.ExcelFile.ExcelSheet;

const TitleButton = styled(Button)`
  margin-left: 5px;
`;

export const ADDRESS_TYPE_ALL = "전체";
export const ADDRESS_TYPE_SUPPORTED = "배송가능지역";
export const ADDRESS_TYPE_NOT_SUPPORTED = "배송불가지역";

const DEFAULT_EXCEL_SCHEME = {
  type: "유형",
  customerName: "수하인명",
  customerMobile: "수하인연락처",
  customerMobile2: "",
  customerAddress: "수하인주소",
  customerAddressDetail: "수하인상세주소",
  customerAddressPostalCode: "우편번호",
  prepaid: "선불",
  productName: "제품명",
  productCategory: "제품종류",
  productPrice: "제품가격",
  productCount: "수량",
  memoFromCustomer: "고객전달사항",
  frontdoorPassword: "공동현관비밀번호",
  orderIdFromCorp: "기업고유번호",
  etc1: "기타사항1",
  etc2: "기타사항2",
  etc3: "기타사항3",
};

const TEXT_STYLE = { numFmt: "@" };

const ADDRESS_PREFIX_LENGTH = 50;

const normalizeAddress = (v) => (v ?? "").trim().replace(/\s+/g, " ");

const makeBaseKey = (name, mobile, address) =>
  [
    name?.trim() ?? "",
    mobile?.replace(/-/g, "") ?? "",
    normalizeAddress(address),
  ].join("__");

const makeFullKey = (name, mobile, address, orderId) =>
  [
    name?.trim() ?? "",
    mobile?.replace(/-/g, "") ?? "",
    normalizeAddress(address),
    String(orderId ?? "").trim(),
  ].join("__");

const detectOriginalDataKeys = (originalData, data, scheme) => {
  if (!originalData?.length || !data?.length) {
    return {
      nameKey: null,
      mobileKey: null,
      mobileKey2: null,
      addressKey: null,
      orderIdKey: null,
    };
  }

  const availableHeaders = Object.keys(originalData[0] ?? {});
  const resolveByScheme = (schemeVal) =>
    schemeVal && availableHeaders.includes(schemeVal) ? schemeVal : null;

  // 1단계: scheme 기반으로 우선 확정
  let nameKey = resolveByScheme(scheme?.customerName);
  let mobileKey = resolveByScheme(scheme?.customerMobile);
  let mobileKey2 = resolveByScheme(scheme?.customerMobile2);
  let addressKey = resolveByScheme(scheme?.customerAddress);
  let orderIdKey = resolveByScheme(scheme?.orderIdFromCorp);

  const sampleData = data[0];

  const verify = (key, dataVal, normalize) => {
    if (!key || !dataVal) return key;
    const matches = originalData.slice(0, 10).some((row) => {
      const rowVal = String(row[key] ?? "").trim();
      const val = String(dataVal).trim();
      return normalize
        ? rowVal.replace(/-/g, "") === val.replace(/-/g, "")
        : rowVal === val;
    });
    return matches ? key : null;
  };

  nameKey = verify(nameKey, sampleData?.receiverName);
  mobileKey = verify(mobileKey, sampleData?.receiverMobile, true); // 하이픈 제거 비교
  addressKey = verify(addressKey, sampleData?.unrefinedAddress);
  orderIdKey = verify(orderIdKey, sampleData?.orderIdFromCorp);

  const needsFallback = !nameKey || !mobileKey || !addressKey;

  if (needsFallback) {
    for (const sampleData of data) {
      for (const sampleRow of originalData) {
        for (const [k, v] of Object.entries(sampleRow)) {
          const strVal = String(v ?? "").trim();

          if (!nameKey && strVal === sampleData.receiverName?.trim()) {
            nameKey = k;
          }
          if (
            strVal.replace(/-/g, "") ===
            sampleData.receiverMobile?.replace(/-/g, "")
          ) {
            if (!mobileKey) {
              mobileKey = k;
            } else if (!mobileKey2 && k !== mobileKey) {
              mobileKey2 = k;
            }
          }
          if (!addressKey && strVal === sampleData.unrefinedAddress?.trim()) {
            addressKey = k;
          }
          if (
            !orderIdKey &&
            sampleData.orderIdFromCorp != null &&
            strVal === String(sampleData.orderIdFromCorp).trim()
          ) {
            orderIdKey = k;
          }
        }
        if (nameKey && mobileKey && addressKey && orderIdKey) break;
      }
      if (nameKey && mobileKey && addressKey && orderIdKey) break;
    }
  }

  return { nameKey, mobileKey, mobileKey2, addressKey, orderIdKey };
};

const buildBaseKeyBookIdSetMap = (data) => {
  const map = new Map();
  const addEntry = (key, bookId) => {
    if (!map.has(key)) map.set(key, new Set());
    map.get(key).add(bookId);
  };
  data.forEach((d) => {
    addEntry(
      makeBaseKey(d.receiverName, d.receiverMobile, d.unrefinedAddress),
      d.bookId,
    );
    if (d.receiverMobile2 && d.receiverMobile2 !== d.receiverMobile) {
      addEntry(
        makeBaseKey(d.receiverName, d.receiverMobile2, d.unrefinedAddress),
        d.bookId,
      );
    }
  });
  return map;
};

const buildBookIdQueueMap = (data, baseKeyBookIdSetMap) => {
  const map = new Map();
  const addEntry = (key, bookId) => {
    if (!map.has(key)) map.set(key, []);
    const queue = map.get(key);
    if (!queue.includes(bookId)) queue.push(bookId);
  };
  data.forEach((d) => {
    const baseKey1 = makeBaseKey(
      d.receiverName,
      d.receiverMobile,
      d.unrefinedAddress,
    );
    const bookIdSet1 = baseKeyBookIdSetMap.get(baseKey1);
    const key1 =
      bookIdSet1?.size === 1
        ? baseKey1
        : makeFullKey(
            d.receiverName,
            d.receiverMobile,
            d.unrefinedAddress,
            d.orderIdFromCorp,
          );
    addEntry(key1, d.bookId);

    if (d.receiverMobile2 && d.receiverMobile2 !== d.receiverMobile) {
      const baseKey2 = makeBaseKey(
        d.receiverName,
        d.receiverMobile2,
        d.unrefinedAddress,
      );
      const bookIdSet2 = baseKeyBookIdSetMap.get(baseKey2);
      const key2 =
        bookIdSet2?.size === 1
          ? baseKey2
          : makeFullKey(
              d.receiverName,
              d.receiverMobile2,
              d.unrefinedAddress,
              d.orderIdFromCorp,
            );
      addEntry(key2, d.bookId);
    }
  });
  return map;
};

// 주소 앞 50자 기준 보조 맵 생성 (성능 최적화)
const buildAddressPrefixMap = (queueMap) => {
  const map = new Map();
  for (const k of queueMap.keys()) {
    const parts = k.split("__");
    // key 형식: name__mobile__address 또는 name__mobile__address__orderId
    const address = parts.slice(2).join("__"); // orderId에 __가 포함될 수 있으므로
    const prefix = address.slice(0, ADDRESS_PREFIX_LENGTH);
    const nameAndMobile = `${parts[0]}__${parts[1]}`;
    const prefixKey = `${nameAndMobile}__${prefix}`;
    if (!map.has(prefixKey)) {
      map.set(prefixKey, k);
    }
  }
  return map;
};

const dequeueBookId = (queueMap, key) => {
  const queue = queueMap.get(key);
  if (!queue || queue.length === 0) return "";
  if (queue.length === 1) return queue[0];
  return queue.shift();
};

const buildMergedData = (originalData, data, scheme) => {
  const { nameKey, mobileKey, mobileKey2, addressKey, orderIdKey } =
    detectOriginalDataKeys(originalData, data, scheme);

  const baseKeyBookIdSetMap = buildBaseKeyBookIdSetMap(data);
  const queueMap = buildBookIdQueueMap(data, baseKeyBookIdSetMap);
  const addressPrefixMap = buildAddressPrefixMap(queueMap);

  return originalData.map((row) => {
    const rawMobile = row[mobileKey]?.toString().trim()
      ? row[mobileKey]
      : (row[mobileKey2] ?? "");

    const normName = (row[nameKey] ?? "").trim();
    const normMobile = rawMobile.toString().trim().replace(/-/g, "");
    const normAddr = normalizeAddress(row[addressKey]);

    const baseKey = `${normName}__${normMobile}__${normAddr}`;
    const bookIdSet = baseKeyBookIdSetMap.get(baseKey);

    let key =
      !bookIdSet || bookIdSet.size <= 1
        ? baseKey
        : makeFullKey(
            row[nameKey],
            rawMobile,
            row[addressKey],
            orderIdKey ? row[orderIdKey] : null,
          );

    // 정확히 매칭되지 않으면 주소 앞 50자 보조 맵으로 재시도 (DB 길이 제한 대응)
    if (!queueMap.has(key) && normAddr.length >= ADDRESS_PREFIX_LENGTH) {
      const prefixKey = `${normName}__${normMobile}__${normAddr.slice(0, ADDRESS_PREFIX_LENGTH)}`;
      const fallbackKey = addressPrefixMap.get(prefixKey);
      if (fallbackKey) {
        key = fallbackKey;
      }
    }

    const bookId = dequeueBookId(queueMap, key);

    return {
      ...row,
      송장번호: bookId,
      운송장번호: bookId,
      촬영코드: bookId,
    };
  });
};

export default function ExcelForPrintOriginalData({
  data,
  originalData,
  headers,
  excelScheme,
  spot,
  addressType = ADDRESS_TYPE_ALL,
}) {
  const { user } = useProfileStore().state;
  const isTenthree = user.username === "tenthree";

  const scheme = excelScheme ?? {
    ...DEFAULT_EXCEL_SCHEME,
    ...spot?.excelSchemeParsed,
  };

  const baseHeaders =
    headers?.length > 0 ? headers : Object.values(scheme).filter(Boolean);

  const columnHeaders = isTenthree
    ? [...baseHeaders.slice(0, 17), "운송장번호", "촬영코드"]
    : [...baseHeaders, "송장번호"];

  const mergedData = buildMergedData(originalData, data, scheme);

  const filteredData = mergedData.filter((row) => {
    const matchedData = data.find(
      (d) => d.bookId === row["송장번호"] || d.bookId === row["운송장번호"],
    );

    if (addressType === ADDRESS_TYPE_SUPPORTED) {
      return matchedData?.addressNotSupported === false;
    }
    if (addressType === ADDRESS_TYPE_NOT_SUPPORTED) {
      return matchedData?.addressNotSupported === true;
    }
    return true;
  });

  const dataSet = [
    {
      columns: columnHeaders.map((header) => ({
        title: header ?? "",
        style: TEXT_STYLE,
        width: { wch: 20 },
      })),
      data: filteredData.map((row) =>
        columnHeaders.map((header) => ({
          value: header != null ? String(row[header] ?? "") : "",
          style: TEXT_STYLE,
        })),
      ),
    },
  ];

  return (
    <ExcelFile
      filename={`두발히어로_송장번호반영_${addressType}_${formatDate(new Date(), "MMdd")}`}
      element={
        <TitleButton type="button" variant="outline-secondary" width="120px">
          송장번호 반영
          <br />
          {addressType === ADDRESS_TYPE_ALL && "(전체)"}
          {addressType === ADDRESS_TYPE_SUPPORTED && "(배송가능지역)"}
          {addressType === ADDRESS_TYPE_NOT_SUPPORTED && "(배송불가지역)"}
        </TitleButton>
      }
    >
      <ExcelSheet dataSet={dataSet} name="ExcelForPrintOriginalData" />
    </ExcelFile>
  );
}
